import Swiper from 'swiper/bundle';
import 'swiper/swiper-bundle.css';

const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

const prefersReducedMotion = () =>
  typeof window.matchMedia === 'function' && window.matchMedia(REDUCED_MOTION_QUERY).matches;

const toInt = (value, fallback) => {
  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) ? parsed : fallback;
};

/**
 * Keep everything inside an inactive slide out of the tab order.
 *
 * Swiper's own a11y module only manages the navigation buttons, so without
 * this a keyboard user tabs straight into the CTA of a slide they cannot see.
 */
const syncSlideFocusability = (swiper) => {
  swiper.slides.forEach((slide) => {
    const isActive = slide.classList.contains('swiper-slide-active');

    slide.querySelectorAll('a[href], button, [tabindex]').forEach((node) => {
      if (isActive) {
        node.removeAttribute('tabindex');
      } else {
        node.setAttribute('tabindex', '-1');
      }
    });
  });
};

/**
 * @param {Document|Element} scope  Where to look for sliders. In the block
 *   editor this is the canvas iframe's document, not the page's.
 * @param {{staticPreview?: boolean}} options  `staticPreview: true` lays the
 *   block out without Swiper. Required in the block editor, and the right
 *   behavior there regardless -- see renderStaticPreview.
 */
/**
 * Lay the block out without Swiper, for the block editor canvas.
 *
 * Two reasons this cannot simply run the real thing. Swiper's mount() tests
 * `el instanceof HTMLElement` against the parent window's constructor, so an
 * element inside the editor's canvas iframe fails the check and init() returns
 * silently. Swiper's stylesheet is injected into the parent document by
 * webpack and never reaches the iframe either.
 *
 * It is also what the editor should do: a carousel that advances on its own
 * walks the slide out from under whoever is editing it. Show the first slide
 * and the full strip, standing still.
 */
const renderStaticPreview = (root) => {
  root.classList.add('is-ready', 'is-static');

  Array.from(root.querySelectorAll('[data-post-slider-thumb]')).forEach((thumb, index) => {
    const isFirst = index === 0;

    thumb.classList.toggle('swiper-slide-thumb-active', isFirst);

    const button = thumb.querySelector('[data-post-slider-thumb-button]');
    if (button) {
      button.setAttribute('aria-current', isFirst ? 'true' : 'false');
    }

    const fill = thumb.querySelector('[data-post-slider-thumb-fill]');
    if (fill) {
      // Nothing is counting down, so a part-filled bar would be a lie.
      fill.style.width = isFirst ? '100%' : '0%';
    }
  });
};

const PostSlider = (scope = document, { staticPreview = false } = {}) => {
  const rootNode = scope && typeof scope.querySelectorAll === 'function' ? scope : document;
  const sliders = Array.from(rootNode.querySelectorAll('[data-post-slider]'));

  if (!sliders.length) {
    return;
  }

  sliders.forEach((root) => {
    const viewport = root.querySelector('.o-post-slider__viewport');
    const slides = Array.from(root.querySelectorAll('.o-post-slider__slide'));

    if (!viewport || slides.length <= 1) {
      // A single slide is already correct as static markup.
      return;
    }

    const reduceMotion = prefersReducedMotion();
    const thumbsEl = root.querySelector('[data-post-slider-thumbs]');
    const thumbButtons = Array.from(root.querySelectorAll('[data-post-slider-thumb-button]'));
    const fills = Array.from(root.querySelectorAll('[data-post-slider-thumb-fill]'));
    const playbackButton = root.querySelector('[data-post-slider-playback]');
    const playbackIcon = root.querySelector('[data-post-slider-playback-icon]');
    const prevButton = root.querySelector('[data-post-slider-prev]');
    const nextButton = root.querySelector('[data-post-slider-next]');
    const dotsEl = root.querySelector('[data-post-slider-dots]');
    const track = root.querySelector('.o-post-slider__track');

    const autoplayRequested = root.dataset.postSliderAutoplay === '1';
    const autoplayDelay = toInt(root.dataset.postSliderAutoplayDelay, 12000);
    const speed = reduceMotion ? 0 : toInt(root.dataset.postSliderSpeed, 500);
    const loop = root.dataset.postSliderLoop === '1';

    // Reduced motion stops the rotation outright, not just the easing.
    const autoplayEnabled = autoplayRequested && !reduceMotion;

    if (staticPreview) {
      renderStaticPreview(root);
      return;
    }

    const configHash = JSON.stringify({
      autoplayEnabled,
      autoplayDelay,
      speed,
      loop,
      slides: slides.length,
      hasThumbs: !!thumbsEl,
      reduceMotion,
    });

    if (viewport.dataset.postSliderInitHash === configHash && viewport.swiper) {
      return;
    }

    if (viewport.swiper && typeof viewport.swiper.destroy === 'function') {
      viewport.swiper.destroy(true, true);
    }

    if (thumbsEl && thumbsEl.swiper && typeof thumbsEl.swiper.destroy === 'function') {
      thumbsEl.swiper.destroy(true, true);
    }

    let thumbsSwiper = null;

    if (thumbsEl) {
      thumbsSwiper = new Swiper(thumbsEl, {
        /*
         * One column per slide, so the whole strip is on screen at once and
         * nothing scrolls. 'auto' would size each thumbnail to its own logo
         * and push the rest out of view. The repeater caps slides at six, and
         * the strip is hidden below 62rem, so the narrowest column is still
         * around 150px.
         */
        slidesPerView: Math.max(1, slides.length),
        spaceBetween: 8,
        speed,
        // Thumbs patches this onto a passed-in instance, but only after the
        // instance already initialized without it. Setting it here means slide
        // progress is tracked from the first frame.
        watchSlidesProgress: true,
        // The thumbnail strip must not loop: Thumbs maps the main slider's
        // realIndex straight onto thumbsSwiper.slides for non-looping strips.
        loop: false,
        slideToClickedSlide: false,
        // Nothing overflows, so there is nothing to drag.
        allowTouchMove: false,
        a11y: { enabled: false },
        breakpoints: {
          768: { spaceBetween: 16 },
        },
      });
    }

    const swiper = new Swiper(viewport, {
      slidesPerView: 1,
      speed,
      loop,
      autoHeight: false,
      watchOverflow: true,
      keyboard: { enabled: true, onlyInViewport: true },
      autoplay: autoplayEnabled
        ? {
            delay: autoplayDelay,
            // Clicking a thumbnail should reposition the slider, not end
            // the rotation for the rest of the visit.
            disableOnInteraction: false,
            pauseOnMouseEnter: true,
          }
        : false,
      navigation: prevButton && nextButton ? { prevEl: prevButton, nextEl: nextButton } : false,
      pagination: dotsEl
        ? {
            el: dotsEl,
            clickable: true,
            bulletClass: 'o-post-slider__dot',
            bulletActiveClass: 'is-active',
          }
        : false,
      thumbs: thumbsSwiper
        ? {
            swiper: thumbsSwiper,
            // Defaults to true, which marks several thumbnails active at once.
            // One slide maps to one thumbnail here.
            multipleActiveThumbs: false,
          }
        : undefined,
      a11y: {
        enabled: true,
        prevSlideMessage: 'Previous slide',
        nextSlideMessage: 'Next slide',
        paginationBulletMessage: 'Go to slide {{index}}',
      },
      on: {
        init: (instance) => {
          syncSlideFocusability(instance);
        },
        slideChange: (instance) => {
          syncSlideFocusability(instance);
        },
      },
    });

    viewport.dataset.postSliderInitHash = configHash;
    // Controls stay hidden until Swiper is driving them, so a failed
    // script leaves readable static content rather than dead buttons.
    root.classList.add('is-ready');

    /*
     * The strip is display:none until `is-ready` lands, so both Swipers were
     * just built against a zero-width container. A numeric slidesPerView
     * divides that width between the columns, so it has to be measured again
     * now the strip is actually laid out.
     */
    if (thumbsSwiper && typeof thumbsSwiper.update === 'function') {
      thumbsSwiper.update();
    }

    // Cached so the autoplayTimeLeft handler, which runs every animation
    // frame, never has to query the DOM.
    let activeFill = null;

    /**
     * Zero every fill and forget which one was active.
     *
     * The fill is written per animation frame from autoplayTimeLeft rather than
     * handed to a CSS transition, so there is no transition to cancel when the
     * rotation pauses or when a visitor has asked for reduced motion.
     */
    const clearFills = () => {
      fills.forEach((fill) => {
        fill.style.width = '0%';
      });
    };

    const setActiveFill = (percent) => {
      if (activeFill) {
        activeFill.style.width = `${percent}%`;
      }
    };

    const syncThumbState = () => {
      if (!thumbsEl) {
        return;
      }

      activeFill = null;

      thumbButtons.forEach((button) => {
        const thumb = button.closest('[data-post-slider-thumb]');
        const isActive = !!thumb && thumb.classList.contains('swiper-slide-thumb-active');

        // Active state is exposed, not only drawn by the fill color.
        button.setAttribute('aria-current', isActive ? 'true' : 'false');

        if (isActive) {
          activeFill = button.querySelector('[data-post-slider-thumb-fill]');
        }
      });
    };

    /**
     * Autoplaying content announces nothing; manual navigation announces politely.
     */
    const syncLiveRegion = () => {
      if (!track) {
        return;
      }

      const running = !!(swiper.autoplay && swiper.autoplay.running && !swiper.autoplay.paused);
      track.setAttribute('aria-live', running ? 'off' : 'polite');
    };

    const syncPlaybackButton = () => {
      if (!playbackButton) {
        return;
      }

      // `running` stays true across a hover or focus pause, which is what the
      // toggle should reflect: only an explicit stop reads as paused.
      const running = !!(swiper.autoplay && swiper.autoplay.running);

      playbackButton.setAttribute('aria-pressed', running ? 'false' : 'true');
      playbackButton.setAttribute(
        'aria-label',
        running ? 'Pause the slideshow' : 'Play the slideshow'
      );

      if (playbackIcon) {
        playbackIcon.textContent = running ? 'pause' : 'play_arrow';
      }
    };

    // Thumbs resyncs the active class on these events; the fill has to follow
    // the same beat or it lags a slide behind the strip.
    swiper.on('slideChange update resize observerUpdate', () => {
      clearFills();
      syncThumbState();
    });

    swiper.on('autoplayTimeLeft', (instance, timeLeft, progress) => {
      // progress counts 1 down to 0, so the fill is its complement.
      setActiveFill(Math.max(0, Math.min(100, (1 - progress) * 100)));
    });

    // Only an explicit stop flips the toggle. Pause and resume fire on hover
    // and focus too, where relabelling the button as "Play" would be a lie.
    swiper.on('autoplayStart', () => {
      syncPlaybackButton();
      syncLiveRegion();
    });

    swiper.on('autoplayStop', () => {
      clearFills();
      syncPlaybackButton();
      syncLiveRegion();
    });

    swiper.on('autoplayPause', () => {
      syncLiveRegion();
    });

    swiper.on('autoplayResume', () => {
      /*
       * Swiper's own pause runs with waitForTransition, so it resumes as soon
       * as a slide transition ends -- including the transition a keyboard user
       * just triggered from a thumbnail, while focus is still inside the
       * slider. Put the pause back rather than let rotation restart under them.
       */
      // Not `document`: in the editor the slider lives in the canvas iframe.
      const focused = root.ownerDocument.activeElement;

      if (root.dataset.postSliderPausedByFocus === '1' && root.contains(focused)) {
        swiper.autoplay.pause();
        return;
      }

      syncLiveRegion();
    });

    thumbButtons.forEach((button) => {
      button.addEventListener('click', () => {
        const index = toInt(button.dataset.postSliderThumbIndex, 0);

        if (loop && typeof swiper.slideToLoop === 'function') {
          swiper.slideToLoop(index);
        } else {
          swiper.slideTo(index);
        }
      });
    });

    if (playbackButton) {
      playbackButton.addEventListener('click', () => {
        if (!swiper.autoplay) {
          return;
        }

        /*
         * `paused` is already true by the time a mouse user reaches this
         * button, because pauseOnMouseEnter fired on the way in. Only
         * `running` tells stopped apart from transiently paused.
         */
        if (swiper.autoplay.running) {
          swiper.autoplay.stop();
        } else {
          // stop() leaves `paused` set. Starting without clearing it freezes
          // calcTimeLeft, and with it the progress fill, for the whole interval.
          swiper.autoplay.paused = false;
          delete root.dataset.postSliderPausedByFocus;
          swiper.autoplay.start();
        }

        syncPlaybackButton();
        syncLiveRegion();
      });
    }

    if (autoplayEnabled) {
      // pauseOnMouseEnter covers the pointer; focus has to be wired by hand.
      root.addEventListener('focusin', () => {
        if (swiper.autoplay && swiper.autoplay.running && !swiper.autoplay.paused) {
          swiper.autoplay.pause();
          root.dataset.postSliderPausedByFocus = '1';
          syncLiveRegion();
        }
      });

      root.addEventListener('focusout', (event) => {
        if (root.dataset.postSliderPausedByFocus !== '1') {
          return;
        }

        if (event.relatedTarget && root.contains(event.relatedTarget)) {
          return;
        }

        delete root.dataset.postSliderPausedByFocus;

        if (swiper.autoplay && swiper.autoplay.running) {
          swiper.autoplay.resume();
          syncLiveRegion();
        }
      });
    }

    clearFills();
    syncThumbState();

    if (reduceMotion) {
      // Nothing is counting down, so an empty bar would read as broken.
      setActiveFill(100);
    }

    syncPlaybackButton();
    syncLiveRegion();
  });
};

export default PostSlider;
